import * as React from "react";
import {
  LoaderFunctionArgs,
  Navigate,
  Outlet,
  RouteObject,
  RouterProvider,
  createHashRouter,
  redirect,
} from "react-router-dom";
import { DateRangeDialog } from "./DateRangeDialog";
import { SuccessMessage } from "./SuccessMessage";
import { PINResetPage } from "./PinResetPage";
import { LocationConfigurationProvider } from "./LocationConfiguration";
import { GlobalConfigHolder, GlobalConfigProvider } from "./GlobalConfig";
import { SupportedFunctionalityProvider } from "./SupportedFunctionality";
import { WindowLocationProvider } from "./WindowLocation";
import { WindowVariablesProvider } from "./WindowVariables";
import { StationLocationProvider } from "./StationLocation";
import { AccountHolder, AccountProvider } from "./Account";
import { SyndeticsConfigProvider } from "./SyndeticsConfig";
import { LoginDialog } from "./LoginDialog";
import { Root, rootLoader } from "./Root";
import { MyAccount, myAccountLoader } from "./pages/account/MyAccount";
import { getLoginRedirect } from "./auth/getLoginRedirect";
import { AccountSummaryPage } from "./pages/account/AccountSummaryPage";
import { AccountLoansPage } from "./pages/account/AccountLoansPage";
import { LoginPage } from "./pages/LoginPage";
import {
  LinkedAccountsPage,
  linkedAccountsLoader,
} from "./pages/account/LinkedAccountsPage";
import { AccountHoldsPage } from "./pages/account/holds/AccountHoldsPage";
import {
  AccountHistoryPage,
  loanHistoryLoader,
} from "./pages/account/AccountHistoryPage";
import { AccountFinesPage } from "./pages/account/AccountFinesPage";
import {
  savedSearchesLoader,
  SavedSearchesPage,
} from "./pages/account/SavedSearchesPage";
import {
  SavedListsPage,
  listLoader,
} from "./pages/account/lists/SavedListPage";
import { AdvancedSearchPage } from "./pages/search/AdvancedSearchPage";
import { BooleanSearchPage } from "./pages/search/BooleanSearchPage";
import { ReservesSearchPage } from "./pages/search/ReservesSearchPage";
import { reservesLoader } from "./pages/search/reservesLoader";
import {
  ReadingLevelSearchPage,
  readingLevelLoader,
} from "./pages/search/ReadingLevelSearchPage";
import { BranchLimitProvider } from "./BranchLimits";
import { BrandingPage } from "./pages/config/BrandingPage";
import { LandingPageConfigPage } from "./pages/config/LandingPageConfigPage";
import {
  TagManagementPage,
  tagsLoader,
} from "./pages/config/TagManagementPage";
import { SearchDatabase, SearchPage } from "./pages/search/SearchPage";
import {
  CreateAccountPage,
  createAccountPageLoader,
} from "./pages/CreateAccountPage";
import { HomePage } from "./pages/HomePage";
import { EditPINPage } from "./pages/account/EditPINPage";
import { EditPasswordPage } from "./pages/account/EditPasswordPage";
import { EditUsernamePage } from "./pages/account/EditUsernamePage";
import { CreateUsernamePage } from "./pages/account/CreateUsernamePage";
import { EditContactInfoPage } from "./pages/account/EditContactInfoPage";
import { ForgotPasswordDialogProvider } from "./ForgotPasswordDialog";
import { LogoutPage } from "./pages/LogoutPage";
import { FourOhFourPage } from "./pages/FourOhFourPage";
import { ListService } from "./pages/account/ListService";
import { LoginContextProvider } from "./LoginContext";
import { PACLocationProvider } from "./PACLocationProvider";
import { DetailsPage } from "./pages/title/DetailsPage";
import { AllCopiesPage } from "./pages/title/AllCopiesPage";
import { ReviewsPage, reviewsPageLoader } from "./pages/title/ReviewsPage";
import { relatedGuard, RelatedPage } from "./pages/title/RelatedPage";
import { moreInfoGuard, MoreInfoPage } from "./pages/title/MoreInfoPage";
import { LocalHoldingsBranchProvider } from "./pages/resource/LocalHoldingsBranch";
import { axios } from "./AxiosInstanceManager";
import {
  ResourcePage,
  resourcePageLoader,
  titleIndexLoader,
} from "./pages/resource/ResourcePage";
import { ToastProvider } from "../contexts/ToastContext";
import { LSResultsPage } from "./pages/search/LSSearchResults";
import { LSSearchProvider } from "./pages/search/ls/LSSearch";
import { AddToListDialogProvider } from "./pages/search/ls/AddToListDialog";
import { AvailabilityProvider } from "../contexts/AvailabilityContext";
import { EResourceAvailabilityProvider } from "../contexts/EResourceAvailabilityContext";
import { ResourceDWR } from "../dto/ResourceDWR";
import {
  EbscoResultsPage,
  EbscoResultsPageLoader,
} from "./pages/search/ebsco/EbscoSearchResults";
import { EbscoSearchProvider } from "./pages/search/ebsco/EbscoSearchProvider";
import { BritannicaSearchProvider } from "./pages/search/britannica/BritannicaSearchProvider";
import { BritannicaResultsPage } from "./pages/search/britannica/BritannicaSearchResults";
import { AuthGuardProvider, secureLoader } from "./auth/AuthGuardProvider";
import { advancedSearchTermAdapter } from "./pages/search/advanced/advancedSearchTermAdapter";
import { advancedSearchNullTermLoader } from "./pages/search/advanced/advancedSearchNullTermLoader";
import { BorrowerLoansView } from "./pages/account/BorrowerLoansView";
import {
  childLoansLoader,
  ChildLoansView,
} from "./pages/account/ChildLoansView";
import { BorrowerHistoryView } from "./pages/account/BorrowerHistoryView";
import {
  childHistoryLoader,
  ChildHistoryView,
} from "./pages/account/ChildHistoryView";
import { FourOhThreePage } from "./pages/FourOhThreePage";
import { ErrorBoundary } from "./pages/ErrorBoundary";
import GaleSearchProvider from "./pages/search/gale/GaleSearchProvider";
import { GaleResultsPage } from "./pages/search/gale/GaleSearchResults";
import WorldBookSearchProvider from "./pages/search/worldbook/WorldBookSearchProvider";
import {
  WorldBookResultsPage,
  WorldBookResultsPageLoader,
} from "./pages/search/worldbook/WorldBookSearchResults";
import { TitleNavigationProvider } from "./nav/TitleNavigationProvider";
import { PlaceHoldProvider } from "./place-hold/PlaceHoldProvider";
import { allCopiesGuard } from "./pages/resource/AllCopies";
import { readingLevelSearchAdapter } from "./pages/search/advanced/reading-levels/readingLevelSearchAdapter";
import { withNormalizedSearchParams } from "./pages/search/withNormalizedSearchParams";
import { holdsLoader } from "./pages/account/holds/holdsLoader";
import { getResourceByHostRecordId } from "./pages/resource/getResourceById";

async function getResourceId(params: URLSearchParams) {
  const resourceId = params.get("resourceid") ?? params.get("resourceId");
  if (!!resourceId) {
    return parseInt(resourceId);
  }
  const hostRecordId = params.get("hostrecordid") ?? params.get("hostRecordId");
  if (!!hostRecordId) {
    try {
      const { data } = await axios.get<ResourceDWR>(
        `/resource/byHostRecordId/${hostRecordId}`
      );
      // Improvement: Cache the data under a resourceId key
      return data.id;
    } catch (e: unknown) {
      return null;
    }
  }
  return null;
}

export type LS2PacRouteObject = RouteObject & {
  handle?: { secured?: boolean; backToLinkTxt?: string };
};

const resourceChildRoutes: LS2PacRouteObject[] = [
  {
    index: true, // This matches ONLY /resource/:titleId with no subpath
    loader: titleIndexLoader,
  },
  {
    path: "details",
    element: <DetailsPage></DetailsPage>,
  },
  {
    path: "copies",
    loader: allCopiesGuard,
    element: <AllCopiesPage></AllCopiesPage>,
  },
  {
    path: "reviews",
    loader: reviewsPageLoader,
    element: <ReviewsPage></ReviewsPage>,
  },
  {
    path: "related",
    loader: relatedGuard,
    element: <RelatedPage></RelatedPage>,
  },
  {
    path: "more",
    loader: moreInfoGuard,
    element: <MoreInfoPage></MoreInfoPage>,
  },
  // If we have a bad path/misspelled route segment redirect to configured default
  {
    path: "*",
    loader: titleIndexLoader,
    element: null,
  },
];

export async function hostRecordLoader({ params }: LoaderFunctionArgs) {
  const hostRecordId = params.hostRecordId;
  try {
    const data = await getResourceByHostRecordId(hostRecordId);
    // Improvement: Cache the data under a resourceId key
    return { resource: data };
  } catch (e: unknown) {
    throw redirect("/404");
  }
}

export const routes: LS2PacRouteObject[] = [
  {
    path: "/",
    element: (
      <AuthGuardProvider>
        <PACLocationProvider>
          <LoginDialog isKids={false}></LoginDialog>
          <TitleNavigationProvider>
            <Root></Root>
          </TitleNavigationProvider>
        </PACLocationProvider>
      </AuthGuardProvider>
    ),
    loader: rootLoader,
    children: [
      {
        path: "hostrecord",
        element: null,
        children: [
          {
            path: ":hostRecordId",
            loader: hostRecordLoader,
            element: <ResourcePage></ResourcePage>,
            children: resourceChildRoutes,
          },
        ],
      },
      {
        path: "resource", // TODO update to resource? YES
        element: null,
        children: [
          {
            path: ":titleId", // TODO update to resourceId
            id: "title", // to access loader data from children, TODO update to resource
            loader: resourcePageLoader,
            element: <ResourcePage></ResourcePage>,
            children: resourceChildRoutes,
          },
          // TODO 404/catchall path
        ],
      },
      {
        id: "myaccount", // so we can useRouteLoaderData("myaccount") from summary, etc.
        path: "/myaccount",
        element: <MyAccount></MyAccount>,
        loader: myAccountLoader,
        errorElement: <ErrorBoundary />,
        children: [
          {
            path: "summary",
            element: <AccountSummaryPage></AccountSummaryPage>,
          },
          {
            path: "edit-pin",
            loader: async ({ params, request }) => {
              const { publicHostInformation } =
                await GlobalConfigHolder.fetchGlobalConfig();
              const canEditPin =
                publicHostInformation?.pinEditable &&
                publicHostInformation?.usePin;
              if (!canEditPin) {
                return redirect("/myaccount/summary");
              }
              return canEditPin;
            },
            element: <EditPINPage></EditPINPage>,
          },
          {
            path: "edit-password",
            loader: async ({ params, request }) => {
              const user = await AccountHolder.fetchUser();
              if (!!user && !user.usernameSet) {
                return redirect("/myaccount/create-username");
              }
              return user;
            },
            element: <EditPasswordPage></EditPasswordPage>,
          },
          {
            path: "edit-username",
            loader: async ({ params, request }) => {
              const user = await AccountHolder.fetchUser();
              if (!!user && !user.usernameSet) {
                return redirect("/myaccount/create-username");
              }
              return user;
            },
            element: <EditUsernamePage></EditUsernamePage>,
          },
          {
            path: "create-username",
            loader: async ({ params, request }) => {
              const user = await AccountHolder.fetchUser();
              if (!!user && user.usernameSet === true) {
                return redirect("/myaccount/edit-username");
              }
              return user;
            },
            element: <CreateUsernamePage></CreateUsernamePage>,
          },
          {
            path: "edit-contact-info",
            loader: async () => {
              const { publicHostInformation, globalSettings } =
                await GlobalConfigHolder.fetchGlobalConfig();
              const canEditContactInfo =
                publicHostInformation?.emailEditable ||
                publicHostInformation?.addressEditable ||
                publicHostInformation?.phoneNumberEditable;
              if (
                globalSettings?.hidePersonalInformation ||
                !canEditContactInfo
              ) {
                return redirect("/myaccount/summary");
              }
              return canEditContactInfo;
            },
            element: <EditContactInfoPage></EditContactInfoPage>,
          },
          {
            path: "linked",
            loader: linkedAccountsLoader,
            element: <LinkedAccountsPage></LinkedAccountsPage>,
          },
          {
            path: "loans",
            handle: { backToLinkTxt: "loans" },
            element: <AccountLoansPage></AccountLoansPage>,
            children: [
              {
                index: true, // default /myaccount/loans
                element: <BorrowerLoansView></BorrowerLoansView>,
              },
              {
                path: "child/:id", // myaccount/loans/child/{id}
                element: <ChildLoansView />,
                loader: childLoansLoader,
              },
            ],
          },
          {
            path: "holds",
            handle: { backToLinkTxt: "requests" },
            loader: holdsLoader,
            element: <AccountHoldsPage></AccountHoldsPage>,
          },
          {
            path: "fines",
            element: <AccountFinesPage></AccountFinesPage>,
            handle: { backToLinkTxt: "fines" },
          },
          {
            path: "history",
            loader: loanHistoryLoader,
            handle: { backToLinkTxt: "loanHistory" },
            element: <AccountHistoryPage></AccountHistoryPage>,
            children: [
              {
                index: true, // default /myaccount/history
                element: <BorrowerHistoryView></BorrowerHistoryView>,
              },
              {
                path: "child/:id", // myaccount/history/child/{id}
                element: <ChildHistoryView />,
                loader: childHistoryLoader,
              },
            ],
          },
          {
            path: "lists",
            id: "lists",
            handle: { backToLinkTxt: "savedLists" },
            element: null,
            children: [
              {
                path: ":listId",
                loader: listLoader,
                element: <SavedListsPage></SavedListsPage>,
              },
              {
                path: "*",
                loader: async ({ request }) => {
                  const { isAuthenticated } =
                    await AccountHolder.fetchAccountData();
                  if (!isAuthenticated) {
                    return redirect(getLoginRedirect(request));
                  }
                  const path = await ListService.getDefaultListPath();
                  return redirect(path);
                },
                element: null,
              },
              {
                path: "",
                loader: async ({ request }) => {
                  const { isAuthenticated } =
                    await AccountHolder.fetchAccountData();
                  if (!isAuthenticated) {
                    return redirect(getLoginRedirect(request));
                  }
                  const path = await ListService.getDefaultListPath();
                  return redirect(path);
                },
                element: null,
              },
            ],
          },
          {
            path: "searches",
            loader: savedSearchesLoader,
            element: <SavedSearchesPage></SavedSearchesPage>,
          },
        ],
      },
      {
        path: "/config",
        id: "config",
        element: <MyAccount></MyAccount>,
        loader: myAccountLoader,
        children: [
          {
            path: "landing-page",
            element: <LandingPageConfigPage></LandingPageConfigPage>,
          },
          {
            path: "tags",
            loader: tagsLoader,
            element: <TagManagementPage></TagManagementPage>,
          },
          {
            path: "branding",
            element: <BrandingPage></BrandingPage>,
          },
        ],
      },
      {
        path: "/advanced-search",
        element: <AdvancedSearchPage></AdvancedSearchPage>,
        children: [
          {
            path: "boolean",
            element: <Outlet></Outlet>,
            children: [
              {
                index: true,
                loader: advancedSearchNullTermLoader,
                element: <BooleanSearchPage></BooleanSearchPage>,
              },
              {
                path: "results",
                id: "advancedSearchResults",
                handle: { backToLinkTxt: "results" },
                loader: withNormalizedSearchParams(
                  advancedSearchNullTermLoader
                ),
                element: (
                  <LSSearchProvider termAdapter={advancedSearchTermAdapter}>
                    <LSResultsPage></LSResultsPage>
                  </LSSearchProvider>
                ),
              },
            ],
          },
          {
            path: "reading-levels",
            element: <Outlet></Outlet>,
            children: [
              {
                index: true,
                loader: readingLevelLoader,
                element: <ReadingLevelSearchPage></ReadingLevelSearchPage>,
              },
              {
                path: "results",
                id: "readingLevelSearchResults",
                handle: { backToLinkTxt: "results" },
                loader: withNormalizedSearchParams(readingLevelLoader),
                element: (
                  <LSSearchProvider termAdapter={readingLevelSearchAdapter}>
                    <LSResultsPage></LSResultsPage>
                  </LSSearchProvider>
                ),
              },
            ],
          },
          {
            path: "",
            element: <Navigate to="boolean" replace={true} />,
          },
        ],
      },
      {
        path: "/reserves",
        loader: withNormalizedSearchParams(reservesLoader),
        element: <ReservesSearchPage></ReservesSearchPage>,
        handle: { backToLinkTxt: "academicReserves" },
      },
      {
        path: "/create-account",
        loader: createAccountPageLoader,
        element: <CreateAccountPage></CreateAccountPage>,
      },
      {
        path: "/login",
        element: <LoginPage></LoginPage>,
      },
      {
        path: "/logout", // this path could be a problem as it overlaps with API
        element: <LogoutPage></LogoutPage>,
      },
      {
        path: "/search",
        element: <SearchPage></SearchPage>,
        handle: { backToLinkTxt: "search" },
        children: [
          {
            path: SearchDatabase.LS,
            id: "basicSearchResults",
            handle: { backToLinkTxt: "results" },
            loader: withNormalizedSearchParams(),
            element: (
              <LSSearchProvider>
                <LSResultsPage></LSResultsPage>
              </LSSearchProvider>
            ),
          },
          {
            path: SearchDatabase.EBSCO,
            loader: secureLoader(EbscoResultsPageLoader),
            handle: {
              secured: true,
            },
            element: (
              <EbscoSearchProvider>
                <EbscoResultsPage></EbscoResultsPage>
              </EbscoSearchProvider>
            ),
          },
          {
            path: SearchDatabase.BRITANNICA,
            loader: secureLoader(),
            handle: {
              secured: true,
            },
            element: (
              <BritannicaSearchProvider>
                <BritannicaResultsPage></BritannicaResultsPage>
              </BritannicaSearchProvider>
            ),
          },
          {
            path: SearchDatabase.GALE,
            loader: secureLoader(),
            handle: { secured: true },
            element: (
              <GaleSearchProvider>
                <GaleResultsPage></GaleResultsPage>
              </GaleSearchProvider>
            ),
          },
          {
            path: SearchDatabase.WORLDBOOK,
            loader: secureLoader(WorldBookResultsPageLoader),
            handle: { secured: true },
            element: (
              <WorldBookSearchProvider>
                <WorldBookResultsPage></WorldBookResultsPage>
              </WorldBookSearchProvider>
            ),
          },
          {
            path: "*",
            loader: async () => {
              return redirect("/404");
            },
            element: null,
          },
        ],
      },
      {
        path: "/reset-pin",
        element: <PINResetPage></PINResetPage>,
      },
      {
        path: "/",
        handle: { backToLinkTxt: "home" },
        element: <HomePage></HomePage>,
      },
      {
        path: "/403",
        element: <FourOhThreePage></FourOhThreePage>,
      },
      {
        path: "/404",
        element: <FourOhFourPage></FourOhFourPage>,
      },
      {
        path: "*",
        loader: async ({ params, request }) => {
          const hash = window.location.hash;

          if (hash) {
            const params = new URLSearchParams(hash.slice(1)); // Remove '#' and parse
            const section = params.get("section");
            const page = params.get("page");
            const tab = params.get("tab");

            // Redirect mappings for old URLs
            if (section === "resetPIN") {
              const borrowerNumber = params.get("borrowerNumber");
              const verificationCode = params.get("verificationCode");
              return redirect(
                `/reset-pin?borrowerNumber=${borrowerNumber}&verificationCode=${verificationCode}`
              );
            }
            if (
              section === "myaccount" &&
              page === "accountSettings" &&
              tab === "accountSummary"
            ) {
              return redirect("/myaccount/summary");
            }
            if (
              section === "myaccount" &&
              page === "accountSettings" &&
              tab === "contactInformation"
            ) {
              return redirect("/myaccount/edit-contact-info");
            }
            if (
              section === "myaccount" &&
              page === "accountSettings" &&
              tab === "linkedAccountsTab"
            ) {
              return redirect("/myaccount/linked");
            }

            if (
              section === "myaccount" &&
              page === "accountSettings" &&
              tab === "updatePin"
            ) {
              return redirect("/myaccount/edit-pin");
            }
            if (
              section === "createezlogin" ||
              (section === "myaccount" &&
                page === "accountSettings" &&
                tab === "updateEZLogin")
            ) {
              return redirect("/myaccount/create-username");
            }
            if (
              section === "myaccount" &&
              page === "accountSettings" &&
              tab === "updateEZUsername"
            ) {
              return redirect("/myaccount/edit-username");
            }
            if (
              section === "myaccount" &&
              page === "accountSettings" &&
              tab === "updateEZPassword"
            ) {
              return redirect("/myaccount/edit-password");
            }
            if (section === "accountsettings") {
              return redirect("/myaccount/summary");
            }
            if (
              section === "myaccount" &&
              (!page || page === "accountSettings") &&
              !tab
            ) {
              return redirect("/myaccount/summary");
            }
            if (section === "myaccount" && page === "accountActivity" && !tab) {
              return redirect("/myaccount/loans");
            }
            if (
              section === "loans" ||
              (section === "myaccount" &&
                page === "accountActivity" &&
                tab === "loans")
            ) {
              return redirect("/myaccount/loans");
            }
            if (
              section === "holds" ||
              section === "requests" ||
              (section === "myaccount" &&
                page === "accountActivity" &&
                tab === "holds")
            ) {
              return redirect("/myaccount/holds");
            }
            if (
              section === "fines" ||
              (section === "myaccount" &&
                page === "accountActivity" &&
                tab === "fines")
            ) {
              return redirect("/myaccount/fines");
            }
            if (
              section === "loanhistory" ||
              (section === "myaccount" &&
                page === "accountActivity" &&
                tab === "loanHistory")
            ) {
              return redirect("/myaccount/history");
            }
            if (
              section === "mylists" ||
              (section === "myaccount" && page === "myLists")
            ) {
              if (tab) {
                return redirect(`/myaccount/lists/${tab}`);
              }
              return redirect(`/myaccount/lists`);
            }
            if (
              section === "myaccount" &&
              page === "mySearches" &&
              tab === "mySearches"
            ) {
              return redirect("/myaccount/searches");
            }
            if (
              section === "myaccount" &&
              page === "configuration" &&
              tab === "landingPageConfigTab"
            ) {
              return redirect("/config/landing-page");
            }
            if (
              section === "myaccount" &&
              page === "configuration" &&
              tab === "tagManagementTab"
            ) {
              return redirect("/config/tags");
            }

            if (section === "resource") {
              const resourceId = await getResourceId(params);
              // Improvement handle null
              // Improvement handle 404
              const view = params.get("view");
              switch (view) {
                case "reviewsDetailsTab":
                  return redirect(`/resource/${resourceId}/reviews`);
                case "allCopiesDetailsTab":
                  return redirect(`/resource/${resourceId}/copies`);
                case "fullDetailsDetailsTab":
                  return redirect(`/resource/${resourceId}/details`);
                case "relatedDetailsTab":
                  return redirect(`/resource/${resourceId}/related`);
                case "lookInsideDetailsTab":
                  return redirect(`/resource/${resourceId}/more`);
                default:
                  // we purposefully leave off the sub path so the loader can resolve
                  return redirect(`/resource/${resourceId}`);
              }
            }

            if (section === "home") {
              return redirect("/");
            }
            if (section === "advancedsearch" && (!page || page === "boolean")) {
              return redirect("/advanced-search/boolean");
            }
            if (section === "advancedsearch" && page === "readingLevels") {
              return redirect("/advanced-search/reading-levels");
            }
            if (section === "advancedsearch" && page === "reserves") {
              return redirect("/reserves");
            }
            if (section === "obrsignup") {
              return redirect("/create-account");
            }
            if (section === "login") {
              return redirect("/login");
            }
            if (section === "search") {
              const redirectParams = new URLSearchParams();
              const db = params.get("db");
              const term = params.get("term");
              const sortKey = params.get("sortKey");

              if (page) {
                redirectParams.set("page", page);
              }
              if (term) {
                redirectParams.set("term", term);
              }

              switch (db) {
                case "ebsco":
                  // #section=search&term=test&page=0&sortKey=date&db=ebsco
                  if (sortKey) {
                    redirectParams.set("sort", sortKey);
                  }
                  return redirect(`/search/ebsco?${redirectParams.toString()}`);
                case "britannica":
                  // #section=search&term=test&page=0&db=britannica

                  return redirect(
                    `/search/britannica?${redirectParams.toString()}`
                  );
                case "gale":
                  // #section=search&term=test&page=0&db=gale

                  return redirect(`/search/gale?${redirectParams.toString()}`);
                case "worldbook":
                  // #section=search&term=test&page=0&db=worldbook
                  return redirect(
                    `/search/worldbook?${redirectParams.toString()}`
                  );
                case "newsbank":
                  return redirect(
                    `/search/newsbank?${redirectParams.toString()}`
                  );
                default:
                  // NOTE: we don't use the view param because they are different in 5.9.0
                  // http://localhost:8081/#section=search&term=paris&page=1&sortKey=NewlyAdded&db=ls2pac&branchFilters=[%221%22]&facetFilters=[{%22facetDisplay%22:%22eBook%22,%22facetValue%22:%22eBook%22,%22facetName%22:%22Format%22}]&audience=[%22Adult%20/%20General%22]

                  // We don't really need to validate here, the loader for /search/ls will handle it
                  const branchFilters = params.get("branchFilters");
                  if (branchFilters) {
                    redirectParams.set("branchFilters", branchFilters);
                  }
                  const facetFilters = params.get("facetFilters");
                  if (facetFilters) {
                    redirectParams.set("facetFilters", facetFilters);
                  }
                  const audience = params.get("audience");
                  if (audience) {
                    // Parse audience array
                    // ["Adult%20/%20General"]
                    try {
                      const audienceArray = JSON.parse(audience);
                      if (
                        typeof audienceArray === "object" &&
                        Array.isArray(audienceArray) &&
                        audienceArray.length === 1
                      ) {
                        redirectParams.set("audience", audienceArray[0]);
                      }
                    } catch (e: unknown) {}
                  }
                  if (page) {
                    redirectParams.set("page", page);
                  }
                  if (sortKey) {
                    redirectParams.set("sort", sortKey);
                  }
                  if (term) {
                    redirectParams.set("term", term);
                  }
                  return redirect(`/search/ls?${redirectParams.toString()}`);
              }
            }
          }

          return null;
        },
        element: <FourOhFourPage></FourOhFourPage>,
      },
    ],
  },
];
const router = createHashRouter(routes, {
  future: {
    v7_relativeSplatPath: true,
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true,
  },
});

export const App: React.FC = () => {
  return (
    <>
      <ToastProvider>
        <LoginContextProvider>
          <DateRangeDialog></DateRangeDialog>
          <SuccessMessage></SuccessMessage>
          <LocationConfigurationProvider>
            <GlobalConfigProvider>
              <SupportedFunctionalityProvider>
                <WindowLocationProvider>
                  <WindowVariablesProvider>
                    <StationLocationProvider>
                      <ForgotPasswordDialogProvider>
                        <AccountProvider>
                          <SyndeticsConfigProvider>
                            <BranchLimitProvider>
                              <LocalHoldingsBranchProvider>
                                <AvailabilityProvider>
                                  <EResourceAvailabilityProvider>
                                    <AddToListDialogProvider>
                                      <PlaceHoldProvider>
                                        <RouterProvider
                                          router={router}
                                          future={{ v7_startTransition: true }}
                                        ></RouterProvider>
                                      </PlaceHoldProvider>
                                    </AddToListDialogProvider>
                                  </EResourceAvailabilityProvider>
                                </AvailabilityProvider>
                              </LocalHoldingsBranchProvider>
                            </BranchLimitProvider>
                          </SyndeticsConfigProvider>
                        </AccountProvider>
                      </ForgotPasswordDialogProvider>
                    </StationLocationProvider>
                  </WindowVariablesProvider>
                </WindowLocationProvider>
              </SupportedFunctionalityProvider>
            </GlobalConfigProvider>
          </LocationConfigurationProvider>
        </LoginContextProvider>
      </ToastProvider>
    </>
  );
};
